import * as THREE from 'https://unpkg.com/three@0.184.0/build/three.module.min.js';
import { MARK, markRadius, insideMark } from './mark-outline.js';
export { THREE, MARK, markRadius, insideMark };

THREE.ColorManagement.enabled = false; // shaders output display values directly

export const PALETTE = { tealBlack: '#071e21', cream: '#f4f0e8', gold: '#e8b86b', ocean: '#004d54' };
export const FONTS = { display: '"DM Sans", system-ui, sans-serif', mono: '"DM Mono", ui-monospace, monospace' };

// Placeholder lot — replace with real metafields before launch.
export const LOT = {
  farm: 'El Diviso', region: 'Huila, Colombia',
  varietal: 'Pink Bourbon', varietalCup: 'floral · stone fruit · long finish',
  process: 'Washed · 36 h', processCup: 'clean acidity · defined sweetness',
  roasted: '28 Aug 2026 · light', roastedCup: 'rest 10–21 days · drop 204 °C',
  altitude: '1,750 m', altitudeCup: 'slow maturation · dense seed · bright acidity',
  altitudeM: 1750, topM: 2150,
};

export const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
export const smooth = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
export const lerp = (a, b, t) => a + (b - a) * t;

export const NOISE_GLSL = `
float hash13(vec3 p){ p = fract(p*0.3183099 + vec3(0.1,0.2,0.3)); p *= 17.0; return fract(p.x*p.y*p.z*(p.x+p.y+p.z)); }
float hash21(vec2 p){ p = fract(p*vec2(234.34, 435.345)); p += dot(p, p+34.23); return fract(p.x*p.y); }
float vnoise(vec3 p){ vec3 i = floor(p); vec3 f = fract(p); f = f*f*(3.0-2.0*f);
  float n000 = hash13(i), n100 = hash13(i+vec3(1.0,0.0,0.0)), n010 = hash13(i+vec3(0.0,1.0,0.0)), n110 = hash13(i+vec3(1.0,1.0,0.0));
  float n001 = hash13(i+vec3(0.0,0.0,1.0)), n101 = hash13(i+vec3(1.0,0.0,1.0)), n011 = hash13(i+vec3(0.0,1.0,1.0)), n111 = hash13(i+vec3(1.0,1.0,1.0));
  return mix(mix(mix(n000,n100,f.x), mix(n010,n110,f.x), f.y), mix(mix(n001,n101,f.x), mix(n011,n111,f.x), f.y), f.z); }
float fbm(vec3 p){ float v = 0.0; float a = 0.5; for (int i = 0; i < 3; i++) { v += a * vnoise(p); p = p * 2.03 + 7.7; a *= 0.5; } return v; }
float fogAt(float d, float density){ return 1.0 - exp(-density*density*d*d); }
`;

export const OUTLINE_GLSL = `
uniform sampler2D uOutline;
float markR(float th){ float n = ${MARK.n}.0; float u = fract(th / 6.2831853); float f = u * n; float i0 = floor(f); float t = f - i0;
  float i1 = mod(i0 + 1.0, n);
  vec4 a = texture2D(uOutline, vec2((i0 + 0.5)/n, 0.5)); vec4 b = texture2D(uOutline, vec2((i1 + 0.5)/n, 0.5));
  float ra = (a.r*255.0*256.0 + a.g*255.0)/65535.0; float rb = (b.r*255.0*256.0 + b.g*255.0)/65535.0; return mix(ra, rb, t); }
`;

export function makeOutlineTexture() {
  const n = MARK.n, data = new Uint8Array(n * 4);
  for (let i = 0; i < n; i++) { const q = Math.round(MARK.r[i] * 65535); data[i * 4] = q >> 8; data[i * 4 + 1] = q & 255; data[i * 4 + 2] = 0; data[i * 4 + 3] = 255; }
  const tex = new THREE.DataTexture(data, n, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.NearestFilter; tex.magFilter = THREE.NearestFilter; tex.wrapS = THREE.ClampToEdgeWrapping; tex.generateMipmaps = false; tex.needsUpdate = true;
  return tex;
}

export function makeSharedUniforms() {
  return {
    uTime: { value: 0 }, uCam: { value: new THREE.Vector3() },
    uLight: { value: new THREE.Vector3(0, 0, 0.8) }, uLightRange: { value: 14 },
    uFogColor: { value: new THREE.Color(PALETTE.tealBlack) }, uFogDensity: { value: 0.045 },
    uWind: { value: new THREE.Vector2() }, uLean: { value: new THREE.Vector2() }, uDpr: { value: 1 }, uDeep: { value: 0 },
    uCream: { value: new THREE.Color(PALETTE.cream) }, uGold: { value: new THREE.Color(PALETTE.gold) }, uBase: { value: new THREE.Color(PALETTE.tealBlack) },
    uOutline: { value: makeOutlineTexture() }, uMarkScale: { value: 3 },
  };
}
export function shareUniforms(shared, extra) {
  const o = {};
  for (const k in shared) o[k] = shared[k]; // by reference: engine updates once
  return Object.assign(o, extra || {});
}

export function catmull(points) { return new THREE.CatmullRomCurve3(points.map(p => new THREE.Vector3(p[0], p[1], p[2])), false, 'centripetal'); }

// ---- text planes: canvas → texture → plane, fogged in-shader (headline lives in the scene; DOM keeps the mirror)
const TEXT_VS = `varying vec2 vUv; varying vec3 vW;
void main(){ vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const TEXT_FS = `uniform sampler2D map; uniform vec3 uFogColor, uCam; uniform float uOpacity, uFogDensity, uNear0, uNear1, uFar; varying vec2 vUv; varying vec3 vW;
void main(){ float d = length(uCam - vW); float vFog = 1.0 - exp(-uFogDensity*uFogDensity*d*d); float vNear = smoothstep(uNear0, uNear1, d) * (1.0 - smoothstep(uFar, uFar + 3.0, d));
  vec4 t = texture2D(map, vUv); float a = t.a * uOpacity * (1.0 - vFog) * vNear; if (a < 0.004) discard; vec3 c = mix(t.rgb, uFogColor, vFog * 0.6); gl_FragColor = vec4(c, a); }`;

export function makeText(lines, o = {}) {
  const { font = 'display', color = PALETTE.cream, height = 0.6, align = 'left', spacing = -0.03, weight = 500, lineGap = 1.15, shared, maxAniso = 8, opacity = 1, near = [1.8, 4.2], far = 1e6 } = o;
  const px = o.px || 200;
  // lines: strings, or { t, s (size ×), f (font), w (weight), c (color), sp (letter-spacing em), gap (space before, × base px) }
  const L = lines.map(l => typeof l === 'string' ? { t: l } : l);
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  const setFont = (l, p) => { const sp = l.sp !== undefined ? l.sp : spacing; ctx.font = `${l.w || weight} ${p}px ${FONTS[l.f || font]}`; ctx.fillStyle = l.c || color; ctx.textBaseline = 'alphabetic'; if ('letterSpacing' in ctx) ctx.letterSpacing = `${(sp * p).toFixed(2)}px`; return sp; };
  const measure = (scale) => { let maxW = 0, hSum = 0; const rows = L.map(l => { const p = px * scale * (l.s || 1); const sp = setFont(l, p); const w = ctx.measureText(l.t).width + Math.max(0, sp * p) * l.t.length; maxW = Math.max(maxW, w); const gap = (l.gap || 0) * px * scale; hSum += gap + p * lineGap; return { p, w, gap }; }); return { maxW, hSum, rows }; };
  let scale = 1, m = measure(1);
  let cw = Math.ceil((m.maxW + px * 0.72) / 64) * 64;
  if (cw > 4096) { scale = 4096 / cw; m = measure(scale); cw = 4096; } // GPU-safe width: shrink, never truncate
  const pad2 = px * 0.36 * scale;
  c.width = cw; c.height = Math.ceil(m.hSum + pad2);
  let y = 0;
  L.forEach((l, i) => { const r = m.rows[i]; setFont(l, r.p); y += r.gap; const x = align === 'center' ? (cw - r.w) / 2 : align === 'right' ? cw - r.w - pad2 : pad2; ctx.fillText(l.t, x, y + r.p * 0.92); y += r.p * lineGap; });
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.NoColorSpace; tex.anisotropy = maxAniso; tex.minFilter = THREE.LinearMipmapLinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = true;
  const k = height / (px * scale), W = cw * k, H = c.height * k;
  const mat = new THREE.ShaderMaterial({ uniforms: shareUniforms(shared, { map: { value: tex }, uOpacity: { value: opacity }, uNear0: { value: near[0] }, uNear1: { value: near[1] }, uFar: { value: far } }), vertexShader: TEXT_VS, fragmentShader: TEXT_FS, transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(W, H), mat);
  mesh.userData = { textW: m.maxW * k, W, H, padW: pad2 * k };
  return mesh;
}
// place by text edge: {left|centerX|right, top, z}
export function placeText(mesh, p) {
  const u = mesh.userData;
  if (p.left !== undefined) mesh.position.x = p.left - u.padW + u.W / 2;
  else if (p.right !== undefined) mesh.position.x = p.right + u.padW - u.W / 2;
  else mesh.position.x = (p.centerX || 0);
  mesh.position.y = p.top - u.H / 2;
  mesh.position.z = p.z;
  return mesh;
}

// ---- dust: static world-space GPU particles, drift + pointer wind in the vertex shader
const DUST_VS = `attribute float aSize; attribute float aSeed;
uniform float uTime, uDpr, uFogDensity, uLightRange; uniform vec2 uWind; uniform vec3 uLight;
varying float vA, vLit;
void main(){
  vec3 p = position; float t = uTime * 0.12;
  p.x += sin(t*1.3 + aSeed*6.28) * 0.25 + uWind.x * (0.4 + 0.6*aSeed);
  p.y += cos(t*0.9 + aSeed*4.1) * 0.2 + uWind.y * (0.4 + 0.6*aSeed);
  p.z += sin(t*0.7 + aSeed*2.0) * 0.3;
  vec4 mv = modelViewMatrix * vec4(p, 1.0); float dist = -mv.z;
  gl_PointSize = clamp(aSize * 16.0 * uDpr / max(dist, 0.25), 1.0, 30.0 * uDpr);
  float Ld = length(uLight - p); float att = 1.0/(1.0 + Ld*Ld/(uLightRange*uLightRange));
  float fog = 1.0 - exp(-uFogDensity*uFogDensity*dist*dist);
  vLit = att; vA = (1.0 - fog) * (0.3 + 0.7*att) * (0.45 + 0.55*aSeed) * smoothstep(0.2, 1.2, dist);
  gl_Position = projectionMatrix * mv;
}`;
const DUST_FS = `uniform vec3 uCream, uGold; varying float vA, vLit;
void main(){ vec2 q = gl_PointCoord - 0.5; float d = length(q); float disc = 1.0 - smoothstep(0.22, 0.5, d);
  vec3 col = mix(uCream, uGold, smoothstep(0.25, 0.75, vLit)); gl_FragColor = vec4(col, disc * vA); }`;

export function makeDust(shared, o = {}) {
  const { count = 4000, box = { x: [-4, 4], y: [-4, 4], z: [-62, 2] }, size = 1 } = o;
  const pos = new Float32Array(count * 3), sz = new Float32Array(count), seed = new Float32Array(count);
  const rnd = (r) => r[0] + Math.random() * (r[1] - r[0]);
  for (let i = 0; i < count; i++) { pos[i * 3] = rnd(box.x); pos[i * 3 + 1] = rnd(box.y); pos[i * 3 + 2] = rnd(box.z); sz[i] = (0.35 + Math.pow(Math.random(), 2.5) * 1.5) * size; seed[i] = Math.random(); }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSize', new THREE.BufferAttribute(sz, 1));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
  const mat = new THREE.ShaderMaterial({ uniforms: shareUniforms(shared), vertexShader: DUST_VS, fragmentShader: DUST_FS, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  const pts = new THREE.Points(geo, mat); pts.frustumCulled = false;
  return pts;
}

// ---- CPU noise (for building the mobile relief)
function h2(x, y) { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
export function noise2(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y); let fx = x - xi, fy = y - yi; fx = fx * fx * (3 - 2 * fx); fy = fy * fy * (3 - 2 * fy);
  const a = h2(xi, yi), b = h2(xi + 1, yi), c = h2(xi, yi + 1), d = h2(xi + 1, yi + 1);
  return lerp(lerp(a, b, fx), lerp(c, d, fx), fy);
}
export function fbm2(x, y) { let v = 0, a = 0.5; for (let i = 0; i < 4; i++) { v += a * noise2(x, y); x = x * 2.02 + 5.3; y = y * 2.02 + 1.7; a *= 0.5; } return v; }

// ---- ground: the lot's contour map continues under the shop — stippled isolines under one soft lamp, drifting with page scroll
const GROUND_VS = `varying vec3 vWorld; void main(){ vec4 w = modelMatrix * vec4(position, 1.0); vWorld = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const GROUND_FS = `${NOISE_GLSL}
uniform vec3 uCam, uFogColor, uCream, uBase; uniform float uFogDensity, uDeep, uTime;
varying vec3 vWorld;
void main(){
  vec2 q = vWorld.xy + vec2(0.0, uDeep * 16.0);
  float h = fbm(vec3(q * 0.085, uTime * 0.006)) * 10.0;
  float d = abs(fract(h + 0.5) - 0.5); float aa = max(fwidth(h), 0.01);
  float major = 1.0 - step(0.5, abs(mod(floor(h + 0.5), 5.0)));
  float w = 0.04 + major * 0.03;
  float line = 1.0 - smoothstep(w, w + aa, d);
  vec2 g = vWorld.xy * 3.2; vec2 lc = fract(g) - 0.5; float m = 1.0 - smoothstep(0.13, 0.25, length(lc));
  float lit = exp(-length(vWorld.xy - uCam.xy) * 0.085);
  vec3 col = uBase * 0.85;
  col = mix(col, uCream * 0.62, line * m * (0.16 + 0.84 * lit) * (0.6 + 0.4 * major));
  float dc = length(uCam - vWorld);
  col = mix(col, uFogColor, fogAt(dc, uFogDensity));
  gl_FragColor = vec4(col, 1.0);
}`;
export function makeGround(shared, o = {}) {
  const { z = -86, size = 220 } = o;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), new THREE.ShaderMaterial({ uniforms: shareUniforms(shared), vertexShader: GROUND_VS, fragmentShader: GROUND_FS }));
  mesh.position.z = z; mesh.frustumCulled = false; return mesh;
}
